import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import CreateIcon from '@mui/icons-material/Create'
import AccountCircle from '@mui/icons-material/AccountCircle'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import StorefrontIcon from '@mui/icons-material/Storefront'
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread'
import CurrentUserAvatar from './CurrentUserAvatar'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import UserAuthContainer from '../Containers/UserAuthContainer'
import UIStateContainer from '../Containers/UIStateContainer'
import useNavigation from '../Utils/navigationUtils'
import ROUTES from '../../routesConfig'

const LoginMenuItem = ({ handleLoginClick }) => (
  <MenuItem
    onClick={handleLoginClick}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#94A3B8' }}>
      <HowToRegIcon />
    </ListItemIcon>
    <ListItemText
      primary="Login"
      primaryTypographyProps={{
        sx: { color: '#CBD5E1', fontWeight: 500 }
      }}
    />
  </MenuItem>
)

const MyProfileMenuItem = React.forwardRef((props, ref) => (
  <MenuItem
    ref={ref}
    onClick={props.handleProfileClick}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#94A3B8' }}>
      <CreateIcon />
    </ListItemIcon>
    <ListItemText
      primary="Profile"
      primaryTypographyProps={{
        sx: { color: '#CBD5E1', fontWeight: 500 }
      }}
    />
  </MenuItem>
))

const SignupMenuItem = ({ handleSignupClick }) => (
  <MenuItem
    onClick={handleSignupClick}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#94A3B8' }}>
      <PersonAddIcon />
    </ListItemIcon>
    <ListItemText
      primary="Sign up"
      primaryTypographyProps={{
        sx: { color: '#CBD5E1', fontWeight: 500 }
      }}
    />
  </MenuItem>
)

const GuestMenuItem = ({ handleGuestClick }) => (
  <MenuItem
    onClick={handleGuestClick}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#94A3B8' }}>
      <PersonAddAlt1Icon />
    </ListItemIcon>
    <ListItemText
      primary="Guest"
      primaryTypographyProps={{
        sx: { color: '#CBD5E1', fontWeight: 500 }
      }}
    />
  </MenuItem>
)

const LogoutMenuItem = ({ handleLogoutClick }) => (
  <MenuItem
    onClick={() => handleLogoutClick()}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#94A3B8' }}>
      <AccountCircle />
    </ListItemIcon>
    <ListItemText
      primary="Logout"
      primaryTypographyProps={{
        sx: { color: '#CBD5E1', fontWeight: 500 }
      }}
    />
  </MenuItem>
)

const MerchantProfileMenuItem = ({ handleMerchantProfileClick }) => (
  <MenuItem
    onClick={handleMerchantProfileClick}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#94A3B8' }}>
      <StorefrontIcon />
    </ListItemIcon>
    <ListItemText
      primary="Merchant Profile"
      primaryTypographyProps={{
        sx: { color: '#CBD5E1', fontWeight: 500 }
      }}
    />
  </MenuItem>
)

const VerifyEmailMenuItem = ({ handleVerifyEmailClick }) => (
  <MenuItem
    onClick={handleVerifyEmailClick}
    sx={{
      color: '#E6E9EF',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.08)'
      }
    }}
  >
    <ListItemIcon sx={{ color: '#FBBF24' }}>
      <MarkEmailUnreadIcon />
    </ListItemIcon>
    <ListItemText
      primary="Email not verified"
      secondary="Open profile to resend verification"
      primaryTypographyProps={{
        sx: { color: '#FDE68A', fontWeight: 600 }
      }}
      secondaryTypographyProps={{
        sx: { color: 'rgba(230,233,239,0.65)' }
      }}
    />
  </MenuItem>
)

const AccountMenu = () => {
  const userAuthContainer = UserAuthContainer.useContainer()
  const uiContainer = UIStateContainer.useContainer()

  const { navigateToProfile, navigateToPathWithReplace, navigateToGuestSignup } = useNavigation()
  const [anchorEl, setAnchorEl] = useState(null)

  const isRegistered = userAuthContainer.isRegisteredAuthed()
  const isAuthed = userAuthContainer.isAuthed()
  const isEmailVerified = userAuthContainer.anearUser()?.emailVerified?.() ?? true
  //
  // Treat any authed-but-not-registered user as a "guest" for header behavior.
  // This avoids relying on the token's acct-type and keeps the UX aligned
  // with how we message guest accounts elsewhere.
  //
  const isGuest = isAuthed && !isRegistered

  const showLoginMenuItem = () => !isAuthed
  const showSignupMenuItem = () => !isAuthed
  const showGuestMenuItem = () => !isAuthed
  const showProfileMenuItem = () => isRegistered
  const showVerifyEmailMenuItem = () => isRegistered && !isEmailVerified
  const showMerchantProfileMenuItem = () => isRegistered && userAuthContainer.isMerchant()
  const showLogoutMenuItem = () => isRegistered
  const merchantProfileHandler = () => {
    handleClose()
    navigateToPathWithReplace(ROUTES.MERCHANT_PROFILE, false)
  }


  const handleClose = () => {
    setAnchorEl(null)
  }

  const clearAndCloseHandler = async (path) => {
    handleClose()
    await userAuthContainer.clearUserAndToken()
    navigateToPathWithReplace(path)
  }

  const profileHandler = () => {
    handleClose()
    navigateToProfile()
  }

  const guestHandler = () => {
    handleClose()
    // The guest item is only offered while signed out, so the current page
    // is a signed-out page (login, about, and so on). After Jump In, go home
    // instead of returning to that page.
    navigateToGuestSignup(
      { pathname: ROUTES.HOME },
      { source: 'account_menu' }
    )
  }

  const handleAvatarClick = (event) => {
    // When the user explicitly engages the account menu, bring the header "lights" back up.
    uiContainer.undimHeader()
    if (isGuest) {
      // Guests go straight to their profile where they can register or logout
      navigateToProfile()
      return
    }
    setAnchorEl(event.currentTarget)
  }

  return (
    <>
      <IconButton
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleAvatarClick}
        color="inherit"
        sx={{
          p: 0.4,
          ml: 1.25,
          opacity: uiContainer.isHeaderDimmed ? 0.35 : 1,
          transition: 'opacity 200ms ease-out, border-color 120ms ease-out, box-shadow 120ms ease-out, background-color 120ms ease-out',
          borderRadius: '999px',
          border: '2px solid rgba(96,165,250,0.7)',
          bgcolor: 'transparent',
          '&:hover': {
            borderColor: 'rgba(96,165,250,1)',
            bgcolor: 'rgba(15,23,42,0.85)',
            boxShadow: '0 0 0 1px rgba(15,23,42,0.9)'
          }
        }}
      >
        <CurrentUserAvatar />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: '#1A1F2E',
            color: '#E6E9EF',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
          }
        }}
      >
        {showVerifyEmailMenuItem() && <VerifyEmailMenuItem handleVerifyEmailClick={profileHandler} />}
        {showProfileMenuItem() && <MyProfileMenuItem handleProfileClick={profileHandler} />}
        {showMerchantProfileMenuItem() && <MerchantProfileMenuItem handleMerchantProfileClick={merchantProfileHandler} />}
        {showLoginMenuItem() && <LoginMenuItem handleLoginClick={() => clearAndCloseHandler(ROUTES.LOGIN)} />}
        {showSignupMenuItem() && <SignupMenuItem handleSignupClick={() => clearAndCloseHandler(ROUTES.SIGNUP)} />}
        {showGuestMenuItem() && <GuestMenuItem handleGuestClick={guestHandler} />}
        {showLogoutMenuItem() && <LogoutMenuItem handleLogoutClick={() => clearAndCloseHandler(ROUTES.LOGOUT)} />}
      </Menu>
    </>
  )
}

export default AccountMenu
